import React, { useCallback, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { debounce } from "lodash";
import "./McKessonPageHeader.scss";

interface ActionButton {
  label: string;
  onClick: () => void;
}

interface McKessonPageHeaderProps {
  title: string;
  disRecords: number;
  totalRecords: number;
  searchVal: { searchText: string; active?: boolean | null };
  setSearchVal: (val: any) => void;
  defaultFilter: { searchText: string; active?: boolean | null };
  actionButtons?: ActionButton[];
  checkboxLabel?: string;
  showCheckbox?: boolean;
  clearButtonText?: string;
  showClearButton?: boolean;
}

const McKessonPageHeader: React.FC<McKessonPageHeaderProps> = ({
  title,
  disRecords,
  totalRecords,
  searchVal,
  setSearchVal,
  defaultFilter,
  actionButtons = [],
  checkboxLabel = "Active",
  showCheckbox = false,
  clearButtonText = "Clear All / Refresh",
  showClearButton = true,
}) => {
  const [localSearchText, setLocalSearchText] = useState(
    searchVal?.searchText ?? ""
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchVal({ ...searchVal, searchText: value });
      }, 500),
    [searchVal, setSearchVal]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearchText(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleActiveChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchVal({
        ...searchVal,
        active: e.target.checked ? true : null,
      });
    },
    [searchVal, setSearchVal]
  );

  const handleClearAll = useCallback(() => {
    setLocalSearchText("");
    setSearchVal({ ...defaultFilter });
  }, [defaultFilter, setSearchVal]);

  return (
    <div className="mckesson-page-header">
      <div className="listing-title-info">
        <h2>{title}</h2>
        <p>
          Displaying {disRecords} of {totalRecords} Results
        </p>
      </div>
      <div className="mckesson-search-controls">
        <div className="listing-searchbar-item-box">
          <input
            type="text"
            min={0}
            className="form-control"
            placeholder="Search"
            onChange={handleSearchChange}
            value={localSearchText}
            autoFocus
            onFocus={(e) => e.target.select()}
          />
        </div>
        {showCheckbox && (
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="checkbox"
              checked={searchVal?.active ?? false}
              onChange={handleActiveChange}
            />
            <label className="form-check-label">{checkboxLabel}</label>
          </div>
        )}
        {actionButtons.map((btn, index) => (
          <Button
            key={index}
            type="button"
            variant="primary"
            onClick={btn.onClick}
          >
            {btn.label}
          </Button>
        ))}
        {showClearButton && (
          <Button
            type="button"
            variant="outline-secondary"
            className="btn-search"
            onClick={handleClearAll}
          >
            {clearButtonText}
          </Button>
        )}
      </div>
    </div>
  );
};

export default McKessonPageHeader;
